import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {Store} from '@ngrx/store';
import {ZahlungSelectors} from '../../../store/selectors/zahlung.selectors';
import {AppState} from '../../../interfaces/app-state.interface';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  FormBlockComponent,
  FormFieldDateComponent,
  FormFieldNumberComponent,
  FormFieldTextAutocompleteComponent,
  FormFieldTextComponent,
  NavigationService
} from '@adnova/jf-ng-components';
import {AsyncPipe, DatePipe, formatCurrency} from '@angular/common';
import {FormBlockPaymentDataFormControls} from './form-block-payment-data-form-controls.interface';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {debounceTime} from 'rxjs/operators';
import {ZahlungActions} from '../../../store/actions/zahlung.actions';
import {BehaviorSubject} from 'rxjs';
import {BankkontoOptionComponent} from './bankkonto-option/bankkonto-option.component';
import {IbanPipe} from '../../../pipes/iban-pipe/iban.pipe';
import {validateIBAN} from 'ngx-iban-validator';
import {BelegActions} from '../../../store/actions/beleg.actions';
import Decimal from 'decimal.js';
import {ValidatorService} from 'angular-iban';


@Component({
  selector: 'zlng-form-block-payment-data',
  templateUrl: './form-block-payment-data.component.html',
  styleUrls: ['./form-block-payment-data.component.scss'],
  standalone: true,
  imports: [
    FormBlockComponent,
    FormFieldTextComponent,
    FormFieldNumberComponent,
    FormFieldDateComponent,
    FormFieldTextAutocompleteComponent,
    AsyncPipe,
    ReactiveFormsModule,
  ],
  providers: [IbanPipe],
})
export class FormBlockPaymentDataComponent implements OnInit {

  private _store = inject(Store<AppState>);
  private _destroyRef = inject(DestroyRef);
  private _ibanPipe = inject(IbanPipe);
  private _datePipe = inject(DatePipe);
  private _navigationService = inject(NavigationService);

  /*
   * INFO: Unicode-Werte der Umlaute.
   * Ä: \u00C4
   * ä: \u00E4
   * Ö: \u00D6
   * ö: \u00F6
   * Ü: \u00DC
   * ü: \u00FC
   * ß: \u00DF
   */
  public _sepaRegex = new RegExp(/^[0-9a-zA-Z\u00C4\u00D6\u00DC\u00E4\u00F6\u00FC\u00DF\/?:().,'+ -]*$/);

  private _formControls: FormBlockPaymentDataFormControls = {
    empfaenger: new FormControl<string>(
      '',
      [
        Validators.required,
        Validators.pattern(this._sepaRegex),
        Validators.minLength(1),
        Validators.maxLength(70),
      ],
    ),
    ibanInput: new FormControl<string>('', [
      Validators.required,
      ValidatorService.validateIban,
    ]),
    bank: new FormControl<string | null>(
      '',
      [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(60),
      ],
    ),
    bic: new FormControl<string | null>(
      '',
      [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(11),
      ],
    ),
    zahlbetrag: new FormControl<Decimal | null>(
      null,
      [
        Validators.required,
        Validators.min(0.01),
      ],
    ),
    verwendungszweck: new FormControl<string | null>(
      '',
      [
        Validators.pattern(this._sepaRegex),
        Validators.maxLength(140),
      ],
    ),
    faelligkeitsdatum: new FormControl<Date | null>(
      null,
    ),
  };
  private _formGroup = new FormGroup(this._formControls);
  private _bankkontoOptions$ = new BehaviorSubject<BankkontoOptionComponent[] | undefined>([]);
  private _isNotEditable = false;

  protected readonly bankkontoOptionComponent = BankkontoOptionComponent;

  get formControls(): FormBlockPaymentDataFormControls {
    return this._formControls;
  }

  get bankkontoOptions$() {
    return this._bankkontoOptions$.asObservable();
  }

  ngOnInit() {
    this._store.select(ZahlungSelectors.angewiesen).pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(angewiesen => {
      if (angewiesen) {
        this._formGroup.disable({emitEvent: false});
      }
    });

    this._store.select(ZahlungSelectors.notEditable).pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(isNotEditable => {
      if (isNotEditable) {
        this._formGroup.disable({emitEvent: false});
      }

      this._isNotEditable = isNotEditable ?? false;
    });

    // INFO: Empfänger Handling

    this._store.select(ZahlungSelectors.empfaenger).pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(empfaenger => {
      const converted = this.convertSpecialCharacters(empfaenger?.bezeichnung || '');
      this._formControls.empfaenger.setValue(converted, {emitEvent: false});
    });

    this._formControls.empfaenger.valueChanges.pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(empfaenger => {
      const converted = this.convertSpecialCharacters(empfaenger || '');

      if (converted !== empfaenger) {
        this._formControls.empfaenger.setValue(converted, {emitEvent: false});
      }

      this._store.dispatch(ZahlungActions.changedFormEmpfaenger({
        empfaengerDto: {
          id: undefined,
          bezeichnung: converted,
        }
      }));
    });

    // INFO: IBAN Handling

    this._store.select(ZahlungSelectors.belegSuggestedIbans).pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(ibanSuggestions => {
      if (!ibanSuggestions || !ibanSuggestions.candidates) return;

      for (const candidate of ibanSuggestions.candidates) {
        this._store.dispatch(ZahlungActions.loadBankinfoFromIban({iban: candidate}));
      }
    });

    this._store.select(ZahlungSelectors.suggestedIbansBankInfo).pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(suggestions => {
      if (!suggestions) return;

      const options = suggestions.map(suggestion => {
        const option = new BankkontoOptionComponent();
        option.id = suggestion.iban;
        option.label = suggestion.iban;
        option.isSelected = suggestion.iban === this._formControls.ibanInput.value;
        option.bank = suggestion.bank;
        return option;
      });

      this._bankkontoOptions$.next(options);

      if (suggestions.length == 1 && this._formControls.ibanInput.value?.length == 0) {
        this._formControls.ibanInput.setValue(suggestions[0].iban, {emitEvent: false});
      }
    });

    this._store.select(ZahlungSelectors.iban).pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(iban => {
      const formattedIban = this._ibanPipe.transform(iban);
      this._formControls.ibanInput.setValue(formattedIban);
    });

    this._formControls.ibanInput.valueChanges.pipe(
      debounceTime(500),
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(iban => {
      const inhaberId = this._navigationService.currentInhaber?.id;
      if (!inhaberId) return;

      if (iban) {
        const formattedIban = this.replaceAllWhitespace(iban);
        this._store.dispatch(ZahlungActions.readBankSuggestions({inhaberId, iban: formattedIban}));

        this._store.dispatch(ZahlungActions.changedFormIban({
          iban: formattedIban || ''
        }));
      } else {
        this._formControls.bank.setValue('', {emitEvent: false});
        this._formControls.bank.enable();

        this._formControls.bic.setValue('', {emitEvent: false});
        this._formControls.bic.enable();

        this._store.dispatch(ZahlungActions.changedFormIban({
          iban: undefined
        }));
      }
    });

    // INFO: Bank Handling

    this._store.select(ZahlungSelectors.bankinfoBank).pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(bankinfoBank => {
      // INFO: Prüfen, ob die IBAN gültig ist.
      const ibanInvalid = !!validateIBAN({value: this._formControls.ibanInput.value || ''})?.ibanInvalid;

      if (ibanInvalid) {

        // INFO: Ist die IBAN grundsätzlich ungültig, dann Bank Eingabe aktivieren.
        this._formControls.bank.enable();
        this._formControls.bank.setValue('', {emitEvent: false});
      } else {

        // INFO: Prüfen, ob wir eine Bank aus der Bankinfo im System haben
        if (bankinfoBank) {

          // INFO: Haben wir die Bank im System, dann wird diese gesetzt und die Eingabe deaktiviert.
          this._formControls.bank.setValue(bankinfoBank);
          this._formControls.bank.disable();
        } else {

          // INFO: Haben wir die Bank nicht im System, dann wird die Eingabe aktiviert und die Bankbezeichnung aus der Zahlung gesetzt.
          this._formControls.bank.enable();
        }
      }
    });

    // INFO: BIC Handling

    this._store.select(ZahlungSelectors.bankinfoBic).pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(bankInfoBic => {
      // INFO: Prüfen, ob die IBAN gültig ist.
      const ibanInvalid = !!validateIBAN({value: this._formControls.ibanInput.value || ''})?.ibanInvalid;

      if (ibanInvalid) {

        // INFO: Ist die IBAN grundsätzlich ungültig, dann BIC Eingabe aktivieren.
        this._formControls.bic.enable();
        this._formControls.bic.setValue('', {emitEvent: false});
      } else {


        // INFO: Prüfen, ob wir eine BIC aus der Bankinfo im System haben
        if (bankInfoBic) {

          // INFO: Haben wir die BIC im System, dann wird diese gesetzt und die Eingabe deaktiviert.
          this._formControls.bic.setValue(bankInfoBic);
          this._formControls.bic.disable();
        } else {
          // INFO: Haben wir die BIC nicht im System, dann wird die Eingabe aktiviert und die BIC aus der Zahlung gesetzt.
          this._formControls.bic.enable();
        }
      }
    });

    // INFO: Bank Handlers

    this._store.select(ZahlungSelectors.bank).pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(bank => {
      if (bank) {
        this._formControls.bank.setValue(bank);
      }
    });

    this._formControls.bank.valueChanges.pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(bank => {
      this._store.dispatch(ZahlungActions.changedFormBank({
        bankbezeichnung: bank || undefined,
      }));
    });

    // INFO: BIC Handlers

    this._store.select(ZahlungSelectors.bic).pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(bic => {
      if (bic) {
        const convertedBic = this.replaceAllWhitespace(bic);
        this._formControls.bic.setValue(convertedBic);
      }
    });

    this._formControls.bic.valueChanges.pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(bic => {
      const bicValue = this.replaceAllWhitespace(bic || '');

      this._store.dispatch(ZahlungActions.changedFormBic({
        bic: bicValue,
      }));
    });

    // INFO: Zahlungsbetrag Handlers

    this._store.select(ZahlungSelectors.betrag).pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(betrag => {
      this._formControls.zahlbetrag.setValue(Decimal(betrag || 0));
    });

    this._formControls.zahlbetrag.valueChanges.pipe(
      takeUntilDestroyed(this._destroyRef),
      debounceTime(500),
    ).subscribe(value => {
      this._store.dispatch(ZahlungActions.changedFormBetrag({
        betrag: value?.toNumber(),
      }));
    });

    // INFO: Verwendungszweck Handlers

    this._store.select(ZahlungSelectors.suggestedPurpose).pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(purposeSuggestion => {
      if (purposeSuggestion && !this._formControls.verwendungszweck.value) {
        this._formControls.verwendungszweck.setValue(purposeSuggestion);
      }
    });

    this._store.select(ZahlungSelectors.verwendungszweck).pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(verwendungszweck => {
      const convertedVerwendungszweck = this.convertSpecialCharacters(verwendungszweck);
      this._formControls.verwendungszweck.setValue(convertedVerwendungszweck);
    });

    this._formControls.verwendungszweck.valueChanges.pipe(
      takeUntilDestroyed(this._destroyRef),
      debounceTime(500),
    ).subscribe(verwendungszweck => {
      this._store.dispatch(ZahlungActions.changedFormVerwendungszweck({
        verwendungszweck: verwendungszweck || undefined,
      }));
    });

    // INFO: Fälligkeitsdatum Handlers

    this._store.select(ZahlungSelectors.faelligkeitsdatum).pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(faelligkeitsdatum => {
      if (!faelligkeitsdatum) return;

      const faelligkeitsdatumDate = new Date(faelligkeitsdatum);
      this._formControls.faelligkeitsdatum.setValue(faelligkeitsdatumDate, {emitEvent: false});
    });

    this._formControls.faelligkeitsdatum.valueChanges.pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(faelligkeitsdatum => {
      const formattedFaelligkeitsdatum = this._datePipe.transform(faelligkeitsdatum, 'yyyy-MM-dd') || undefined;
      this._store.dispatch(ZahlungActions.changedFormFaelligkeitsdatum({
        faelligkeitsdatum: formattedFaelligkeitsdatum,
      }));
    });

    this._formGroup.statusChanges.pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(() => {
      /*
       * INFO:
       * Ist eine Zahlung nicht editierbar, dann wurde sie in ADNOVA+ erstellt.
       * Eine solche Zahlung, ist immer gültig, kann jedoch nicht bearbeitet werden.
       * Das bedeutet, dass wir die Validität des Formulars in diesem Fall bewusst auf true setzen müssen.
       */
      if (!this._isNotEditable) {
        this._store.dispatch(ZahlungActions.updatePaymentBlockValidity({valid: this._formGroup.valid}));
      } else {
        this._store.dispatch(ZahlungActions.updatePaymentBlockValidity({valid: true}));
      }
    });

    this._formGroup.valueChanges.pipe(
      takeUntilDestroyed(this._destroyRef),
    ).subscribe(() => {
      this._store.dispatch(ZahlungActions.updatePaymentBlockChanged({changed: true}));
    });
  }

  getEmpfaengerValidatorMessage(): string {
    const formControl = this._formControls?.empfaenger;
    if (!formControl?.touched) return '';

    const errors = formControl.errors;
    if (errors?.required) {
      return 'Bitte einen Zahlungsempfänger eingeben.';
    } else if (errors?.maxlength) {
      return 'Dieses Feld darf max. 70 Zeichen enthalten.';
    } else if (errors?.pattern) {
      return 'Dieses Feld darf nur Buchstaben, Zahlen, Leerzeichen und die Zeichen / ? : ( ) . , \' + - enthalten.';
    }
    return '';
  }

  getIbanValidatorMessage(): string {
    const formControl = this._formControls?.ibanInput;
    if (!formControl?.touched) return '';

    const errors = formControl.errors;
    if (errors?.required) {
      return 'Bitte eine IBAN eingeben.';
    } else if (errors?.iban) {
      return 'Bitte eine valide IBAN eingeben.';
    }

    return '';
  }

  getBankValidatorMessage(): string {
    const defaultMessage = 'Wird automatisch ausgefüllt.';

    const formControl = this._formControls?.bank;
    if (!formControl?.touched) return defaultMessage;

    const errors = formControl.errors;
    if (errors?.required) {
      return 'Bitte eine Bankbezeichnung eingeben.';
    } else if (errors?.maxlength) {
      return 'Dieses Feld darf max. 60 Zeichen enthalten.';
    }
    return defaultMessage;
  }

  getBicValidatorMessage(): string {
    const defaultMessage = 'Wird automatisch ausgefüllt.';

    const formControl = this._formControls?.bic;
    if (!formControl?.touched) return defaultMessage;

    const errors = formControl.errors;
    if (errors?.required) {
      return 'Bitte eine BIC eingeben.';
    } else if (errors?.minlength) {
      return 'Dieses Feld muss min. 8 Zeichen enthalten.';
    } else if (errors?.maxlength) {
      return 'Dieses Feld darf max. 11 Zeichen enthalten.';
    }
    return defaultMessage;
  }

  getZahlbetragValidatorMessage(): string {
    const formControl = this._formControls?.zahlbetrag;
    if (!formControl?.touched) return '';

    const errors = formControl.errors;
    if (errors?.required) {
      return 'Bitte einen Zahlbetrag eingeben.';
    }
    return '';
  }

  getVerwendungszweckValidatorMessage(): string {
    const formControl = this._formControls?.verwendungszweck;
    if (!formControl?.touched) return '';

    const errors = formControl.errors;
    if (errors?.maxlength) {
      return 'Dieses Feld darf max. 140 Zeichen enthalten.';
    } else if (errors?.pattern) {
      return 'Dieses Feld darf nur Buchstaben, Zahlen, Leerzeichen und die Zeichen / ? : ( ) . , \' + - enthalten.';
    }
    return '';
  }

  /**
   * Entfernt alle Leerzeichen aus dem String.
   *
   * @param value Der zu modifizierende String.
   */
  replaceAllWhitespace(value: string): string {
    return value.replace(/\s/g, '');
  }

  /**
   * Ersetzt die Umlaute im Text.
   *
   * @param text
   * @private
   */
  private convertSpecialCharacters(text: string) {
    return text
      .replace(/ä/g, 'ae')
      .replace(/Ä/g, 'Ae')
      .replace(/ö/g, 'oe')
      .replace(/Ö/g, 'Oe')
      .replace(/ü/g, 'ue')
      .replace(/Ü/g, 'Ue')
      .replace(/ß/g, 'ss')
      .replace(/&/g, 'und')
      .replace(/\t/g, ' ');
  }


  doHighlightWords(formControl: FormControl<any>) {
    const value = formControl.value;
    let highlightText = '';

    if (typeof value === 'string') {
      highlightText = value;
    } else if (value instanceof Decimal) {
      const numberValue = value.toNumber();
      highlightText = formatCurrency(numberValue, 'de-de', '');
    } else if (value instanceof Date) {
      const datum = value as Date;
      highlightText = this._datePipe.transform(datum, 'dd.MM.YYYY') || '';
    }

    this._store.dispatch(BelegActions.highlightText({highlightText}));
  }

  doClearHighlighting() {
    this._store.dispatch(BelegActions.highlightText({highlightText: ''}));
  }
}
